import { useEffect } from "react";
import { useTranslation } from "../i18n/useTranslation";

interface LetterPadProps {
  onLetter: (letter: string) => void;
  onBackspace: () => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function LetterPad({
  onLetter,
  onBackspace,
  onSubmit,
  submitDisabled = false,
}: LetterPadProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key.length === 1 && /^[a-zA-Z]$/.test(e.key)) {
        onLetter(e.key.toUpperCase());
      } else if (e.key === "Backspace") onBackspace();
      else if (e.key === "Enter" && !submitDisabled) onSubmit();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onLetter, onBackspace, onSubmit, submitDisabled]);

  const baseBtn =
    "h-11 rounded-lg font-mono text-base font-medium border transition-all duration-100 active:scale-95";
  const letterBtn = `${baseBtn} bg-bg-2 border-[var(--color-line)] hover:border-[var(--color-line-strong)] text-fg-0`;

  return (
    <div className="max-w-md mx-auto w-full flex flex-col gap-2 px-2">
      <div className="grid grid-cols-9 gap-1.5">
        {ALPHABET.slice(0, 9).map((l) => (
          <button key={l} type="button" className={letterBtn} onClick={() => onLetter(l)}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-9 gap-1.5">
        {ALPHABET.slice(9, 18).map((l) => (
          <button key={l} type="button" className={letterBtn} onClick={() => onLetter(l)}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-9 gap-1.5">
        {ALPHABET.slice(18).map((l) => (
          <button key={l} type="button" className={letterBtn} onClick={() => onLetter(l)}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          type="button"
          className={`${baseBtn} bg-bg-2 border-[var(--color-line)] hover:border-[var(--color-line-strong)] text-fg-2 text-sm`}
          onClick={onBackspace}
          aria-label={t("common.backspace")}
        >
          ⌫
        </button>
        <button
          type="button"
          disabled={submitDisabled}
          className={[
            baseBtn,
            "text-sm font-semibold",
            submitDisabled
              ? "bg-bg-2 border-[var(--color-line)] text-fg-3 opacity-50 cursor-not-allowed"
              : "bg-cyan border-cyan text-bg-1 hover:opacity-90",
          ].join(" ")}
          onClick={onSubmit}
        >
          {t("common.enter")}
        </button>
      </div>
    </div>
  );
}
